import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';

import {
  clientsItem,
  homeownerItems,
  messagesItem,
  propertiesItem,
  tasksDrawerItem,
  todayItem,
  toolsItems,
} from './navigation/navItems';
import { CollapsedNavItem, ExpandedNavGroup, NavDivider } from './navigation/NavPrimitives';
import { useDueDiligenceNavItems } from './navigation/useDueDiligenceNavItems';
import { PropertyAdvisors } from './PropertyAdvisors';
import { PropertySelector } from './PropertySelector';
import { SidebarShell } from './SidebarShell';
import { UserSwitcher } from './UserSwitcher';

import { ViewSwitcher } from '@/components/navigation/ViewSwitcher';
import { useDashboardLayout } from '@/context/DashboardLayoutProvider';
import { useUploaderContext } from '@/context/UploaderProvider';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';

export const AdvisorSidebar = () => {
  const { isLeftSidebarCollapsed, openTasksDrawer, openInbox } = useDashboardLayout();
  const { totalUnread } = useUnreadMessages();
  const { stepProcessing } = useUploaderContext();
  const isProcessing = stepProcessing >= 1 && stepProcessing < 5;
  const dueDiligenceItems = useDueDiligenceNavItems();
  // Today belongs to no one property: the rail carries no property picker there
  const onToday = useLocation().pathname.startsWith('/dashboard/today');

  const manageItems = [
    todayItem,
    clientsItem,
    { ...propertiesItem, processing: isProcessing },
    tasksDrawerItem(openTasksDrawer),
    messagesItem(openInbox, totalUnread || undefined),
  ];

  if (isLeftSidebarCollapsed) {
    return (
      <SidebarShell
        topContent={
          onToday ? undefined : (
          <>
            <PropertySelector collapsed />
            <Box
              sx={{
                mx: '10px',
                mt: '4px',
                mb: '15px',
                borderTop: '1px solid var(--tx-line)',
                width: 'calc(100% - 20px)',
              }}
            />
          </>
          )
        }
        footer={
          <>
            <Box
              sx={{
                mx: '10px',
                mt: '8px',
                mb: '4px',
                borderTop: '1px solid var(--tx-line)',
              }}
            />
            <UserSwitcher collapsed showEngagement={false} />
          </>
        }>
        {manageItems.map((item) => (
          <CollapsedNavItem
            key={item.path}
            path={item.path}
            label={item.label}
            icon={item.icon}
            disabled={item.disabled}
            external={item.external}
            badge={item.badge}
            onClick={item.onClick}
          />
        ))}
        <NavDivider />
        {dueDiligenceItems.map((item) => (
          <CollapsedNavItem
            key={item.path}
            path={item.path}
            label={item.label}
            icon={item.icon}
            disabled={item.disabled}
            external={item.external}
          />
        ))}
        <NavDivider />
        {homeownerItems.map((item) => (
          <CollapsedNavItem
            key={item.path}
            path={item.path}
            label={item.label}
            icon={item.icon}
            disabled={item.disabled}
            external={item.external}
          />
        ))}
        {toolsItems.map((item) => (
          <CollapsedNavItem
            key={item.path}
            path={item.path}
            label={item.label}
            icon={item.icon}
            disabled={item.disabled}
            external={item.external}
          />
        ))}
      </SidebarShell>
    );
  }

  return (
    <SidebarShell
      topContent={
        <>
          {/* the context tile: the property (the switcher) first, then the client with their deal and time left; the
              engagement line runs along its bottom edge */}
          <Box sx={{ position: 'relative', overflow: 'hidden', p: '4px 4px 6px', borderRadius: '16px', backgroundColor: 'rgba(var(--tx-ink-rgb), 0.05)', mb: '12px' }}>
            <PropertySelector inTile />
            <Box sx={{ borderTop: '1px solid var(--tx-line)', mx: '10px' }} />
            <PropertyAdvisors inTile />
          </Box>
        </>
      }
      footer={
        <>
          <Box sx={{ px: 0, pb: '8px' }}>
            <ViewSwitcher />
          </Box>
          <Box sx={{ pb: '16px' }}>
            <UserSwitcher showEngagement={false} />
          </Box>
        </>
      }>
      <Box
        data-testid="advisor-sidebar-nav"
        sx={{ display: 'flex', flexDirection: 'column', px: 0, py: 0, mt: '10px' }}>
        <ExpandedNavGroup label="Manage" items={manageItems} />
        <ExpandedNavGroup label="Due Diligence" items={dueDiligenceItems} />
        <ExpandedNavGroup label="Homeowner" items={homeownerItems} />
        <ExpandedNavGroup label="Tools" items={toolsItems} />
      </Box>
    </SidebarShell>
  );
};
